import { useEffect, useRef, useState } from 'react'
import { createCaptureQueue } from '../data/queue.js'
import { loadDay } from '../data/day.js'
import { todayISO, fromISO } from '../data/workdate.js'
import './workday.css'
import './dailyreport.css'

import backChevron from '../assets/figma/workday/back-chevron.svg'
import micIcon from '../assets/figma/report/mic.svg'
import cameraIcon from '../assets/figma/report/camera-green.svg'
import autosaveIcon from '../assets/figma/report/autosave.svg'

/*
 * Daily Report — implemented from the approved Figma prototype frame 5:50.
 * Structure, spacing, type and colour are the Figma values — see
 * dailyreport.css.
 *
 * 5:96 ("Reports are saved automatically") is a behavioural requirement, not
 * decoration: the draft is persisted on every keystroke, so nothing typed is
 * ever lost by closing the app. Submitting stamps the report; it does not
 * clear or lock the text.
 *
 * Add Photo (5:85) uses the device camera through the OS picker, which is the
 * same mechanism the day screen already used. The custom full-screen
 * viewfinder in frame 7:2 is NOT implemented in Build 001 — it is deferred
 * whole, not reinterpreted, and recorded in the Upgrade Register.
 *
 * Voice capture (5:77) renders as designed but is disabled: Build 001 captures
 * no audio, and a control that looks live but does nothing is worse than one
 * that is plainly unavailable.
 */
export default function DailyReport({ adapter, session, onHome, autoPhoto = false }) {
  const [text, setText] = useState('')
  const [photos, setPhotos] = useState([])
  const [submittedAt, setSubmittedAt] = useState(null)
  const [loaded, setLoaded] = useState(false)
  const [busy, setBusy] = useState(false)
  const [ack, setAck] = useState('')
  const fileRef = useRef(null)

  const day = loadDay()
  const daySession = day?.session ?? session
  const employeeId = daySession?.employee?.id ?? null
  const date = todayISO()

  const [queue] = useState(() => createCaptureQueue(adapter, {}))
  useEffect(() => () => queue.dispose(), [queue])

  useEffect(() => {
    let alive = true
    if (!employeeId || typeof adapter.getDailyReport !== 'function') { setLoaded(true); return }
    adapter.getDailyReport(employeeId, date)
      .then((r) => {
        if (!alive) return
        setText(r.text ?? '')
        setPhotos(r.photos ?? [])
        setSubmittedAt(r.submittedAt ?? null)
        setLoaded(true)
      })
      .catch(() => { if (alive) setLoaded(true) })
    return () => { alive = false }
  }, [adapter, employeeId, date])

  // Arriving from Home's TAKE PHOTO row: open the camera straight away, once.
  const autoFiredRef = useRef(false)
  useEffect(() => {
    if (!autoPhoto || !loaded || autoFiredRef.current) return
    autoFiredRef.current = true
    fileRef.current?.click()
  }, [autoPhoto, loaded])

  // 5:96 — autosave. Persists the draft, never the submitted stamp.
  function autosave(patch) {
    if (!employeeId || typeof adapter.saveDailyReport !== 'function') return
    adapter.saveDailyReport(employeeId, date, patch).catch(() => { /* quota — the
      draft stays in memory for this session rather than breaking the screen */ })
  }

  function onText(v) {
    setText(v)
    autosave({ text: v })
  }

  async function onPhotoPicked(e) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setBusy(true)
    const next = [...photos, file.name]
    setPhotos(next)
    autosave({ photos: next })
    const res = await queue.capturePhoto(daySession, new Date().toISOString(), file.name)
    setBusy(false)
    setAck(res.syncStatus === 'queued'
      ? 'Photo saved on your device, will sync automatically.'
      : 'Photo added.')
  }

  function removePhoto(i) {
    const next = photos.filter((_, n) => n !== i)
    setPhotos(next)
    autosave({ photos: next })
  }

  async function submit() {
    if (busy || !employeeId) return
    setBusy(true)
    const now = new Date().toISOString()
    try {
      const res = await adapter.submitDailyReport(daySession, date, now)
      if (res.success) {
        setSubmittedAt(res.submittedAt)
        setAck('Report submitted. You can still correct it.')
      } else {
        setAck('Saved on your device — the office will pick it up.')
      }
    } catch {
      setAck('Saved on your device — the office will pick it up.')
    }
    setBusy(false)
  }

  return (
    <div className="wd" data-figma="5:50">
      <div className="wd-header">
        <button type="button" className="wd-back" onClick={onHome}>
          <img className="wd-back__icon" src={backChevron} alt="" />
          Home
        </button>
        <p className="wd-title">DAILY REPORT</p>
        <div className="wd-header__spacer" />
      </div>

      <div className="wd-body wd-body--dr">
        <p className="dr-today" data-figma="5:69">Today</p>
        <div className="dr-gap-3" />
        <p className="dr-date" data-figma="5:71">{longDate(date)}</p>
        <div className="dr-gap-16" />

        <div className="dr-capture" data-figma="5:73">
          <textarea
            className="dr-text"
            data-figma="5:75"
            placeholder="What did you work on today?"
            aria-label="What did you work on today?"
            value={text}
            onChange={(e) => onText(e.target.value)}
            disabled={!loaded}
          />
          {/* 5:77 — rendered as designed, disabled: Build 001 captures no audio. */}
          <button type="button" className="dr-voice" data-figma="5:77" disabled
                  title="Voice capture is not available in this build"
                  aria-label="Voice capture — not available in this build">
            <img className="dr-voice__icon" src={micIcon} alt="" />
          </button>
        </div>

        <div className="dr-gap-20" />
        <p className="dr-photos-label" data-figma="5:83">Photos</p>
        <div className="dr-gap-10" />

        <button type="button" className="dr-addphoto" data-figma="5:85"
                disabled={busy} onClick={() => fileRef.current?.click()}>
          <img className="dr-addphoto__icon" src={cameraIcon} alt="" />
          Add Photo
        </button>

        {photos.length > 0 && (
          <ul className="dr-photolist">
            {photos.map((name, i) => (
              <li key={`${name}-${i}`}>
                <span>{name}</span>
                <button type="button" onClick={() => removePhoto(i)}>Remove</button>
              </li>
            ))}
          </ul>
        )}

        <div className="wd-grow" />

        {ack && (
          <>
            <p className="wd-note" role="status">{ack}</p>
            <div className="dr-gap-12" />
          </>
        )}

        <button type="button" className="dr-submit" data-figma="5:90"
                disabled={busy || !loaded} onClick={submit}>
          {submittedAt ? 'Submit Update' : 'Submit Report'}
        </button>
        <div className="dr-gap-12" />

        <div className="dr-autosave" data-figma="5:93">
          <img className="dr-autosave__icon" src={autosaveIcon} alt="" />
          Reports are saved automatically.
        </div>
      </div>

      <input ref={fileRef} type="file" accept="image/*" capture="environment"
             style={{ display: 'none' }} onChange={onPhotoPicked} />
    </div>
  )
}

/* 5:71 shows "August 9, 2026" — the long form, not the ISO stamp. */
function longDate(iso) {
  try {
    return fromISO(iso).toLocaleDateString([], { month: 'long', day: 'numeric', year: 'numeric' })
  } catch { return iso }
}
